import { useEffect, useState } from "react";
import { ArrowUp, ArrowDown, Trash2, Plus, RotateCcw, RefreshCw, Loader2, ListChecks } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const isCustom = (k) => k.startsWith("custom");
const autoGrow = (el) => { if (el && el.offsetWidth) { el.style.height = "auto"; el.style.height = `${el.scrollHeight}px`; } };
// Re-measure when the row becomes visible or its width changes (collapsed sections measure 0).
const growRef = (el) => {
  if (!el || el._ro) return;
  el._ro = new ResizeObserver(() => autoGrow(el));
  el._ro.observe(el);
  autoGrow(el);
};
const fetchDefault = (ctx) => api.post("/jobs/phone-plan/default", ctx).then(r => r.data.plan);

function PlanRow({ item, i, last, onPatch, onMove, onDelete }) {
  return (
    <li data-testid={`phone-plan-item-${i}`} className={`flex items-start gap-1.5 sm:gap-2 p-2 rounded-lg border border-[color:var(--bd-sub)] ${item.enabled ? "bg-[color:var(--bg-elev)]" : "opacity-50"}`}>
      <span className="hidden sm:block w-5 text-[11px] font-mono text-slate-500 pt-2 text-right">{i + 1}</span>
      <input type="checkbox" data-testid={`phone-plan-toggle-${i}`} checked={item.enabled} onChange={(e) => onPatch({ enabled: e.target.checked })}
        title={item.enabled ? "Turn off" : "Turn on"} className="mt-2.5 accent-indigo-500" />
      <textarea data-testid={`phone-plan-text-${i}`} value={item.q} rows={1} ref={growRef} onChange={(e) => { autoGrow(e.target); onPatch({ q: e.target.value }); }}
        placeholder="Type your question…" className="flex-1 min-w-0 min-h-[2rem] px-2 py-1.5 text-sm leading-snug rounded-md bg-transparent border border-transparent focus:border-indigo-500 outline-none resize-none overflow-hidden" />
      <div className="flex shrink-0 items-center pt-1">
        <button type="button" data-testid={`phone-plan-up-${i}`} disabled={i === 0} onClick={() => onMove(-1)} className="p-1 rounded hover:bg-slate-500/10 disabled:opacity-30"><ArrowUp className="w-3.5 h-3.5" /></button>
        <button type="button" data-testid={`phone-plan-down-${i}`} disabled={last} onClick={() => onMove(1)} className="p-1 rounded hover:bg-slate-500/10 disabled:opacity-30"><ArrowDown className="w-3.5 h-3.5" /></button>
        <button type="button" data-testid={`phone-plan-delete-${i}`} onClick={onDelete} className="p-1 rounded hover:bg-rose-500/10 text-rose-400"><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
    </li>
  );
}

// Ordered phone-screen questions. value [] = auto plan from job details; non-empty = recruiter-edited.
export default function PhonePlanEditor({ value, onChange, ctx }) {
  const edited = (value || []).length > 0;
  const [auto, setAuto] = useState([]);
  const [busy, setBusy] = useState(false);
  const ctxKey = JSON.stringify(ctx);

  useEffect(() => {
    if (edited) return;
    const t = setTimeout(() => fetchDefault(ctx).then(setAuto).catch(() => {}), 400);
    return () => clearTimeout(t);
  }, [ctxKey, edited]); // eslint-disable-line react-hooks/exhaustive-deps

  const items = edited ? value : auto;
  const set = (next) => onChange(next);
  const patch = (i, p) => set(items.map((it, j) => (j === i ? { ...it, ...p } : it)));
  const move = (i, d) => { const n = [...items]; [n[i], n[i + d]] = [n[i + d], n[i]]; set(n); };
  const del = (i) => set(items.filter((_, j) => j !== i));
  const add = () => set([...items, { key: `custom-${Date.now()}`, q: "", enabled: true }]);
  const refresh = async () => {
    setBusy(true);
    try {
      const fresh = await fetchDefault(ctx);
      set([...fresh, ...items.filter(it => isCustom(it.key) && it.q.trim())]);
      toast.success("Plan updated from job details — your own questions were kept");
    } catch { toast.error("Couldn't rebuild the plan"); } finally { setBusy(false); }
  };

  return (
    <div data-testid="phone-plan-editor" className="rounded-xl border border-[color:var(--bd-sub)] p-3 space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs text-slate-400 uppercase tracking-wider"><ListChecks className="w-3.5 h-3.5" /> Phone question plan</div>
        <span data-testid="phone-plan-mode" className={`tp-chip ${edited ? "tp-chip-amber" : "tp-chip-cyan"}`}>{edited ? "Edited" : "Auto from job details"}</span>
      </div>
      <div className="text-[11px] text-slate-500">The AI asks these in order, skips anything already answered and may ask one short follow-up on a vague answer.</div>
      <ol className="space-y-1.5">
        {items.map((it, i) => (
          <PlanRow key={it.key + i} item={it} i={i} last={i === items.length - 1}
            onPatch={(p) => patch(i, p)} onMove={(d) => move(i, d)} onDelete={() => del(i)} />
        ))}
        {!items.length && <li className="text-xs text-slate-500 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Building plan…</li>}
      </ol>
      <div className="flex flex-wrap gap-2 pt-1">
        <button type="button" data-testid="phone-plan-add" onClick={add} className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-[color:var(--bd-sub)] hover:border-indigo-500"><Plus className="w-3.5 h-3.5" /> Add question</button>
        {edited && <button type="button" data-testid="phone-plan-refresh" onClick={refresh} disabled={busy} className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-[color:var(--bd-sub)] hover:border-indigo-500 disabled:opacity-50">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Update from job details</button>}
        {edited && <button type="button" data-testid="phone-plan-reset" onClick={() => set([])} className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-[color:var(--bd-sub)] hover:border-rose-500 text-slate-400"><RotateCcw className="w-3.5 h-3.5" /> Reset to default</button>}
      </div>
    </div>
  );
}

// Fields the backend needs to build the default plan, from a job form's string state.
export const planCtx = (f, salaryFromInput) => ({
  must_haves: (f.must_haves || "").split(",").map(s => s.trim()).filter(Boolean),
  knockout_questions: (f.knockout_questions || "").split("\n").map(s => s.trim()).filter(Boolean),
  salary_currency: f.salary_currency, salary_unit: f.salary_unit || null,
  salary_min: salaryFromInput(f.salary_min, f.salary_unit) || null,
  salary_max: salaryFromInput(f.salary_max, f.salary_unit) || null,
  city: f.city || null, country_code: f.country_code || null, remote: !!f.remote,
});

export const planPayload = (plan) => (plan || []).filter(p => p.q.trim()).map(({ key, q, enabled }) => ({ key, q: q.trim(), enabled }));
